import React from 'react';
import { useWorkspaceData } from './AccountsPeopleTabs';
import MeetingHistorySection from '../AccountWorkspace/MeetingHistorySection';

/*
 * AccountsMeetingsTab — GAS's "Meeting Notes" tab (ladder S4).
 *
 * Reuses the workspace's MeetingHistorySection rather than reimplementing it,
 * so recordings, summaries and action items behave identically wherever they
 * appear.
 *
 * The account-id problem the ladder recorded as a blocker turned out to be
 * already solved: `useWorkspaceData` resolves the accounts-table id this page
 * lacks, on the SAME react-query key the workspace uses — so the payload is
 * fetched once and shared, not fetched twice.
 *
 * Boundary note: approved meeting SUMMARIES only. Transcripts never reach a
 * GAS-reachable surface, and nothing here changes that — this is the TekStak
 * side, reading the same store.
 */
export default function AccountsMeetingsTab({ selectedDepartment }) {
  const { data, isLoading, error } = useWorkspaceData(selectedDepartment);
  const accountId = data?.account_id || null;
  const accountName = selectedDepartment?.acronym
    || selectedDepartment?.official_name
    || null;

  if (isLoading) {
    return <div className="accounts-panel-note">Loading meetings…</div>;
  }
  if (error) {
    return <div className="accounts-error">{error.message || 'Failed to load meetings'}</div>;
  }
  // No accounts row yet means nobody has acted on this agency — the workspace
  // creates one lazily on the first write. Say that rather than showing an
  // empty meetings list, which reads as "no meetings" instead of "no account".
  if (!accountId) {
    return (
      <div className="accounts-panel-note">
        No account record for {accountName} yet — add a contact or a note first
        and meetings will attach to it.
      </div>
    );
  }

  return (
    <MeetingHistorySection
      accountId={accountId}
      accountName={accountName}
      contacts={data?.contacts || []}
    />
  );
}
